import {
    FieldTypedValueBase,
    MetricsDashboardData, MetricsDashboardDataRow, MetricsDashboardGraph,
    MetricsDashboardGraphDimensionFormat,
    MetricsDashboardGraphFilter, MetricsDashboardGraphFilterComparer,
    MetricsDashboardGraphTypeAggregation, MetricsDashboardGraphTypeDisplayType,
} from "../../../__generated__/api";
import { Time, toUTCInputDate, toUTCInputHourMinute } from "../../../date";

export interface GraphProps {
    graph: MetricsDashboardGraph;
    data: MetricsDashboardData;
    columnIndexMap: Record<string, number>;
    labelMap: Record<string, string>;
}

export function formatValueWithDisplay(value: number | undefined, display: MetricsDashboardGraphTypeDisplayType) {
    let textValue;
    if (value == null) {
        textValue = "-";
    } else if (display === MetricsDashboardGraphTypeDisplayType.Float) {
        textValue = value.toFixed(2);
    } else if (display === MetricsDashboardGraphTypeDisplayType.Currency) {
        textValue = `$ ${value.toFixed(2)}`;
    } else if (display === MetricsDashboardGraphTypeDisplayType.Percentage) {
        textValue = `${(value * 100).toFixed(2)}%`;
    } else if (display === MetricsDashboardGraphTypeDisplayType.Duration) {
        const currentHour = Math.floor((value ?? 0) / Time.HOUR);
        const currentMin = Math.floor((value ?? 0) / Time.MINUTE) % 60;
        textValue = `${currentHour}h ${currentMin}m`;
    } else {
        textValue = Math.round(value);
    }
    return textValue;
}

// eslint-disable-next-line max-len
export function getMetricValue(values: string[], aggregation = MetricsDashboardGraphTypeAggregation.Average): number | undefined {
    if (aggregation === MetricsDashboardGraphTypeAggregation.Average) {
        return values.isNotEmpty ? values.average(row => parseFloat(row)) : undefined;
    }
    if (aggregation === MetricsDashboardGraphTypeAggregation.Count) {
        return values.length;
    }
    if (aggregation === MetricsDashboardGraphTypeAggregation.CountDistinct) {
        return values.dedupe().length;
    }
    if (aggregation === MetricsDashboardGraphTypeAggregation.Max) {
        return values.maxValue(row => parseFloat(row));
    }
    if (aggregation === MetricsDashboardGraphTypeAggregation.Min) {
        return values.minValue(row => parseFloat(row));
    }
    if (aggregation === MetricsDashboardGraphTypeAggregation.Median) {
        return values.median(row => parseFloat(row));
    }
    if (aggregation === MetricsDashboardGraphTypeAggregation.Sum) {
        return values.sum(row => parseFloat(row));
    }
    return undefined;
}

/* eslint-disable max-len */
const FILTERS: Record<MetricsDashboardGraphFilterComparer, ((value: string | undefined | null, filterValue: FieldTypedValueBase | undefined) => boolean)> = {
    [MetricsDashboardGraphFilterComparer.Equal]: (value, filterValue) => value === (filterValue?.string ?? filterValue?.number),
    [MetricsDashboardGraphFilterComparer.NotEqual]: (value, filterValue) => value !== (filterValue?.string ?? filterValue?.number),
    [MetricsDashboardGraphFilterComparer.GreaterThan]: (value, filterValue) => value != null && value > (filterValue?.string ?? filterValue?.number ?? 0),
    [MetricsDashboardGraphFilterComparer.GreaterThanEqual]: (value, filterValue) => value != null && value >= (filterValue?.string ?? filterValue?.number ?? 0),
    [MetricsDashboardGraphFilterComparer.LessThan]: (value, filterValue) => value != null && value < (filterValue?.string ?? filterValue?.number ?? 0),
    [MetricsDashboardGraphFilterComparer.LessThanEqual]: (value, filterValue) => value != null && value <= (filterValue?.string ?? filterValue?.number ?? 0),
    [MetricsDashboardGraphFilterComparer.BeginsWith]: (value, filterValue) => value != null && value.toString().startsWith(filterValue?.string as string),
    [MetricsDashboardGraphFilterComparer.HasAttribute]: value => !!value,
    [MetricsDashboardGraphFilterComparer.NoAttribute]: value => !value,
    [MetricsDashboardGraphFilterComparer.In]: (value, filterValue) => value != null && (filterValue?.options ?? filterValue?.string?.split(",") ?? []).includes(value),
    [MetricsDashboardGraphFilterComparer.NotIn]: (value, filterValue) => value == null || !(filterValue?.options ?? filterValue?.string?.split(",") ?? []).includes(value),
    [MetricsDashboardGraphFilterComparer.Between]: () => false,
};
/* eslint-enable max-len */

export function filter(
    data: MetricsDashboardDataRow[],
    filters: MetricsDashboardGraphFilter[] | undefined | null,
    columnMap: Record<string, number>,
) {
    if (!filters) {
        return data;
    }
    return data.filter(row => filters.some(filterItem => {
        const index = columnMap[filterItem.fieldId];
        const value = row.values[index];
        const comparer = FILTERS[filterItem.comparer ?? MetricsDashboardGraphFilterComparer.Equal];
        return comparer(value, filterItem.value as FieldTypedValueBase | undefined);
    }));
}

export function formatDate(date: Date, dimension: MetricsDashboardGraphDimensionFormat) {
    if (dimension === MetricsDashboardGraphDimensionFormat.Year) {
        return [date.getUTCFullYear()].join("-");
    } if (dimension === MetricsDashboardGraphDimensionFormat.YearMonth) {
        return [date.getUTCFullYear(), date.getUTCMonth() + 1].join("-");
    } if (dimension === MetricsDashboardGraphDimensionFormat.YearMonthDay) {
        return toUTCInputDate(date);
    } if (dimension === MetricsDashboardGraphDimensionFormat.YearMonthDayHour) {
        const hours = `${date.getUTCHours().toString().padStart(2, "0")}:00`;
        return `${toUTCInputDate(date)} ${hours}`;
    } if (dimension === MetricsDashboardGraphDimensionFormat.YearMonthDayHourMinute) {
        return `${toUTCInputDate(date)} ${toUTCInputHourMinute(date)}`;
    }
    return "Invalid Format";
}
